import React from 'react';
import { Avatar, Card, CardContent, Container, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Slider from 'react-slick';
import testimonialImg1 from '../../assets/images/testimonial/1.png';
import testimonialImg2 from '../../assets/images/testimonial/2.png';
import testimonialImg3 from '../../assets/images/testimonial/6.png';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import './style/Testimonial.css';

// ============================================================
// STYLED COMPONENTS
// ============================================================

const TestimonialCard = styled(Card)({
  borderRadius: "16px",
  padding: "28px",
  margin: "10px",
  height: "320px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  border: "1px solid #eef2f7",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
  backgroundColor: "#ffffff",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: "linear-gradient(90deg, #000080, #0a4fd5)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 15px 45px rgba(10, 79, 213, 0.15)",
    borderColor: "rgba(10, 79, 213, 0.2)",

    "&::before": {
      opacity: 1,
    },
  },
});

const StyledAvatar = styled(Avatar)({
  width: 60,
  height: 60,
  border: "3px solid #ffffff",
  boxShadow: "0 4px 15px rgba(10, 79, 213, 0.2)",
  transition: "all 0.3s ease",

  "&:hover": {
    transform: "scale(1.08) rotate(5deg)",
    boxShadow: "0 8px 25px rgba(10, 79, 213, 0.3)",
  },
});

const QuoteIcon = styled(Box)({
  position: "absolute",
  top: 20,
  right: 24,
  color: "rgba(10, 79, 213, 0.08)",
  fontSize: 60,
  lineHeight: 1,
  pointerEvents: "none",
});

// ============================================================
// TESTIMONIAL DATA
// ============================================================

const testimonials = [
  {
    text: "In the above testimonial, a customer named Jeanine shares her experience with Briogeo's products. While the post is scattered with too many product mentions, it takes full advantage of its real estate by allowing the writer to tell",
    author: 'Guy Hawkins',
    role: 'Trade Boss',
    image: testimonialImg1,
    rating: 5,
  },
  {
    text: "The above testimonial is about Martha Chumo, who taught herself to code in one summer. This testimonial example works because it allows prospective customers to see themselves in Codeacademy's current customer base.",
    author: 'Belal Hossain',
    role: 'Trade Genius',
    image: testimonialImg2,
    rating: 5,
  },
  {
    text: "This testimonial shows the journey of a user who benefited from our services, highlighting the key points and making a strong case for potential customers.",
    author: 'John Doe',
    role: 'Trading Expert',
    image: testimonialImg3,
    rating: 4,
  }
];

// ============================================================
// SLIDER SETTINGS
// ============================================================

const settings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 2,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 4000,
  pauseOnHover: true,
  arrows: false,
  responsive: [
    {
      breakpoint: 900,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

// ============================================================
// STAR RATING COMPONENT
// ============================================================

const StarRating = ({ rating }) => {
  return (
    <Box sx={{ display: "flex", gap: 0.3, mb: 2 }}>
      {[...Array(5)].map((_, index) =>
        index < rating ? (
          <StarIcon
            key={index}
            sx={{ color: "#ffc107", fontSize: 18 }}
          />
        ) : (
          <StarBorderIcon
            key={index}
            sx={{ color: "#ffc107", fontSize: 18 }}
          />
        )
      )}
    </Box>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const Testimonial = () => {
  return (
    <Container
      maxWidth="lg"
      sx={{
        paddingTop: { xs: "50px", md: "70px" },
        paddingBottom: { xs: "50px", md: "70px" },
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "700px",
          margin: "0 auto 50px",
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 700,
            color: "#000000",
            fontSize: { xs: "1.6rem", sm: "1.9rem", md: "2.2rem" },
            lineHeight: 1.3,
            mb: 1.5,
          }}
        >
          <span style={{ color: "#000080" }}>Connect With</span> Our Clients
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "#666666",
            lineHeight: 1.7,
            fontSize: "16px",
          }}
        >
          We love connecting with our clients to hear about their experiences
          and how we can improve.
        </Typography>
      </Box>

      {/* Carousel */}
      <Slider {...settings}>
        {testimonials.map((testimonial, index) => (
          <div key={index}>
            <TestimonialCard>
              {/* Quote Icon Background */}
              <QuoteIcon>
                <FormatQuoteIcon sx={{ fontSize: 60 }} />
              </QuoteIcon>

              <CardContent sx={{ p: 0, position: "relative", zIndex: 1 }}>
                {/* Star Rating */}
                <StarRating rating={testimonial.rating} />

                {/* Testimonial Text */}
                <Typography
                  variant="body1"
                  sx={{
                    color: "#4a4a5a",
                    fontStyle: "italic",
                    lineHeight: 1.8,
                    fontSize: "14.5px",
                    mb: 3,
                    display: "-webkit-box",
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  "{testimonial.text}"
                </Typography>
              </CardContent>

              {/* Author Info */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  pt: 2,
                  borderTop: "1px solid #eef2f7",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <StyledAvatar
                  src={testimonial.image}
                  alt={testimonial.author}
                />
                <Box sx={{ ml: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a2e",
                      fontSize: "15px",
                      lineHeight: 1.3,
                    }}
                  >
                    {testimonial.author}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#0a4fd5",
                      fontWeight: 600,
                      fontSize: "13px",
                      mt: 0.3,
                    }}
                  >
                    {testimonial.role}
                  </Typography>
                </Box>
              </Box>
            </TestimonialCard>
          </div>
        ))}
      </Slider>
    </Container>
  );
};

export default Testimonial;